import type { PlanCardPlan } from '../../components/PlanCard/PlanCard';

/**
 * Emotional, founder-led display copy per billing slug.
 * API still returns slugs and IDs; we only override name, tagline, and feature bullets for marketing.
 * Stripe/checkout slugs stay: growth | scale | enterprise.
 */
export const EMOTIONAL_PLAN_DISPLAY: Record<
  string,
  Pick<PlanCardPlan, 'name' | 'tagline' | 'features'>
> = {
  growth: {
    name: 'Design Partner Pilot',
    tagline: 'For product leaders who want to test Relix on real Jira delivery workflows.',
    features: [
      'Founder-led onboarding',
      'Product Delivery Audit',
      'Jira analytics review',
      'Release and forecast review',
      'Commitment review',
      'Feedback loop to shape the product',
    ],
  },
  scale: {
    name: 'Team',
    tagline: 'For one product team that wants delivery clarity.',
    features: [
      'Multiple Jira projects in one focused workspace',
      'Capacity, forecast, release, and commitment views',
      'Calendar and absence signals where configured',
      'Azure AD / SSO where configured',
      'Executive-friendly reports and exports',
      'Priority support',
    ],
  },
  enterprise: {
    name: 'Enterprise',
    tagline: 'For organizations preparing for governed human and AI work.',
    features: [
      'Everything in Team, plus rollout for larger product orgs',
      'Higher limits for products and members',
      'Security and procurement-friendly terms',
      'Custom integrations and API access',
      'Named customer success and roadmap alignment',
    ],
  },
};

export function applyEmotionalPlanDisplayNames(plans: PlanCardPlan[]): PlanCardPlan[] {
  return plans.map((p) => {
    const o = EMOTIONAL_PLAN_DISPLAY[p.slug];
    if (!o) return p;
    return {
      ...p,
      name: o.name,
      tagline: o.tagline,
      features: o.features,
    };
  });
}

/**
 * Mirrors `supabase/migrations/014_billing_tables.sql` seed rows.
 * Used when GET /api/billing/plans fails or returns no rows so the pricing UI still renders.
 */
export const FALLBACK_BILLING_PLANS: PlanCardPlan[] = applyEmotionalPlanDisplayNames([
  {
    id: 'fallback-plan-growth',
    slug: 'growth',
    name: 'Growth',
    tagline: '',
    price_monthly_cents: 99000,
    price_annual_cents: 990000,
    max_products: 3,
    max_team_members: 25,
    features: [],
  },
  {
    id: 'fallback-plan-scale',
    slug: 'scale',
    name: 'Scale',
    tagline: '',
    price_monthly_cents: 199000,
    price_annual_cents: 1990000,
    max_products: 10,
    max_team_members: 100,
    features: [],
  },
  {
    id: 'fallback-plan-enterprise',
    slug: 'enterprise',
    name: 'Enterprise',
    tagline: '',
    price_monthly_cents: 0,
    price_annual_cents: 0,
    max_products: 999,
    max_team_members: 999,
    features: [],
  },
]);

export function resolveBillingPlansFromApi(
  apiPlans: PlanCardPlan[] | undefined | null
): PlanCardPlan[] {
  const list = apiPlans ?? [];
  const base = list.length > 0 ? list : FALLBACK_BILLING_PLANS;
  return applyEmotionalPlanDisplayNames(base);
}
